import cn from 'classnames/bind';
import React, { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '@funtech-int/yango-shared';
import queryString, { ParsedQuery } from 'query-string';

import { Link } from '@/shared/components/Link/Link';
import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { adminFamilyAddRoute } from '@/scenarios/profiles/routes';
import { useLocale } from '@/shared/hooks/useLocale';
import { useI18n } from '@/scenarios/profiles/hooks/useI18n';
import { clientConfig } from '@/config/client';

import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';

import styles from './AddToFamilyButton.module.css';

const cx = cn.bind(styles);

interface Props {
    isAdmin: boolean;
    viewVariant?: 'vertical' | 'horizontal';
}

const FAMILY_PATH = 'family';

const { idHost } = clientConfig;

function getIdLink(path: string, query: ParsedQuery = {}): string {
    return queryString.stringifyUrl({
        url: new URL(`/${path}`, idHost).toString(),
        query,
    });
}
export const AddToFamilyButton: FC<Props> = ({ isAdmin, viewVariant = 'vertical' }) => {
    const analytics = useAnalytics(createAnalyticsEvents);

    const { ref } = useInView({
        onChange(inView: boolean) {
            if (inView) {
                analytics.inviteShowed();
            }
        },
    });

    const { lang } = useLocale();
    const isHorizontalView = viewVariant === 'horizontal';
    const i18n = useI18n();

    const component = (
        <>
            <div className={cx('button')} ref={ref} />
            <p className={cx('text')}>{i18n('add_to_family_button__text')}</p>
        </>
    );

    if (isAdmin) {
        return (
            <Link
                className={cx('root', {
                    'root-horizontal': isHorizontalView,
                })}
                href={getIdLink(FAMILY_PATH, {
                    colorScheme: 'dark',
                    dialog: 'create-invite',
                    lang,
                })}
                target="_blank"
                rel="noreferrer"
                onClick={analytics.inviteClicked}
            >
                {component}
            </Link>
        );
    }

    return (
        <RouterLink
            className={cx('root', {
                'root-horizontal': isHorizontalView,
            })}
            to={adminFamilyAddRoute()}
            onClick={analytics.inviteClicked}
        >
            {component}
        </RouterLink>
    );
};
